import { useState } from 'react';
import { Plus, MapPin, Truck } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { MapPicker } from '@/components/ui/MapPicker';
import { useVehicles } from '@/hooks/useVehicles';
import toast from 'react-hot-toast';

export function VehiclesPage() {
  const { vehicles, loading, create } = useVehicles();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ label: '', address: '', latitude: 0, longitude: 0, city: '', state: '' });
  const [saving, setSaving] = useState(false);

  if (loading) return <LoadingSpinner />;

  const handleCreate = async () => {
    if (!form.label) { toast.error('Nombre del vehículo es obligatorio'); return; }
    try {
      setSaving(true);
      await create({
        vehicle_id: crypto.randomUUID(),
        label: form.label,
        address: form.address,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
        city: form.city || null,
        state: form.state || null,
        is_primary: vehicles.length === 0,
      });
      setShowAdd(false);
      setForm({ label: '', address: '', latitude: 0, longitude: 0, city: '', state: '' });
      toast.success('Vehículo registrado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al registrar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl tracking-wider">VEHÍCULOS</h1>
        <Button size="sm" onClick={() => setShowAdd(true)}>
          <Plus size={16} className="mr-1" /> Agregar
        </Button>
      </div>

      {vehicles.length === 0 ? (
        <EmptyState
          title="Sin vehículos"
          description="Registra tu primer vehículo para empezar a recibir oportunidades de entrega."
          icon={<Truck size={28} className="text-gray-400" />}
          action={<Button onClick={() => setShowAdd(true)}>Registrar vehículo</Button>}
        />
      ) : (
        <div className="space-y-3">
          {vehicles.map(v => (
            <Card key={v.id} hover className="flex items-center gap-3">
              <div className="w-12 h-12 bg-brand-black rounded-xl flex items-center justify-center flex-shrink-0">
                <Truck size={22} className="text-brand-yellow" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold truncate">{v.label || 'Vehículo'}</p>
                  {v.is_primary && (
                    <span className="text-[10px] bg-brand-yellow px-1.5 py-0.5 rounded-full font-medium">Principal</span>
                  )}
                </div>
                {v.address && (
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={12} /> {v.address}
                  </p>
                )}
                {v.city && <p className="text-xs text-gray-400">{v.city}, {v.state}</p>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Nuevo Vehículo">
        <div className="space-y-4">
          <Input
            label="Nombre / Etiqueta"
            placeholder="Mi camioneta"
            value={form.label}
            onChange={(e) => setForm(p => ({ ...p, label: e.target.value }))}
          />
          <Input
            label="Ciudad"
            placeholder="CDMX"
            value={form.city}
            onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))}
          />
          <Input
            label="Estado"
            placeholder="Ciudad de México"
            value={form.state}
            onChange={(e) => setForm(p => ({ ...p, state: e.target.value }))}
          />
          <MapPicker
            latitude={form.latitude}
            longitude={form.longitude}
            address={form.address}
            onSelect={(data) => setForm(p => ({ ...p, ...data }))}
          />
          <Button fullWidth onClick={handleCreate} loading={saving}>
            Registrar vehículo
          </Button>
        </div>
      </Modal>
    </div>
  );
}
